import {
  IBETARenderer,
  SpecialNeedsRenderer,
  SpecialNeedsValueGetter
} from '@dataGrid/components/cellRenderer';
  import {
    formatDeliveryWindow,
    isValidTimeValue,
    mergeUserConfig,
    formatDate,
  } from '@dataGrid/gridUtilities';
  import { COLUMN_CONFIG } from "../../constants";
  import appConstants from "./../../../../../appConstants";
  import {
    DateTimeFilter,
    MultiSelectFilter,
    StaticFilter,
  } from '@dataGrid/components/columnFilters';
  import { format } from "date-fns";
  import { createCustomCheckboxColumn } from "@dataGrid/CustomGridCheckBox/createCustomCheckboxColumn";
  import { CustomerNameCellRenderer } from "../../../../common/CustomerMergeIndicator/CustomerNameCell";

  export default class GridConfig {
    constructor(configuration) {
      const config = configuration || {};
      this.name = "GridConfiguration";
      this.config = GridConfig.getConfiguration(config);
    }
    static getConfiguration({ columnConfig, userConfig }) {
      const defaultConfig = GridConfig.getDefaultConfiguration({ columnConfig });
      return mergeUserConfig({
        defaultConfig,
        userConfig,
      });
    }
  
    static isFirstColumn(params) {
      const displayedColumns = params.columnApi.getAllDisplayedColumns();
      return displayedColumns[0] === params.column;
    }
  
    static getSpecialNeedsOption(params) {
      return params.context.specialNeedsOptions;
    }
  
    static getAPTWindowConfig(columnConfig) {
      if (columnConfig === COLUMN_CONFIG.SAIA) {
        return {
          headerName: "Delivery Window",
          headerTooltip: "Delivery Window",
          valueGetter: (params) =>
            params.data.shipmentCta || params.data.deliveryWindow.join(","),
          valueFormatter: formatDeliveryWindow,
        };
      }
      return {
        headerName: "APT Window",
        headerTooltip: "Appointment Window",
        valueGetter: (params) => params.data.shipmentCta,
        valueFormatter: (params) => {
          if (!isValidTimeValue(params.value)) {
            return;
          }
          return params.value;
        },
      };
    }

    static formatDateTime(params) {
      return params.value ? format(params.value, appConstants.DATETYPES.DATETIME.format) : "";
    }
  
    static getDefaultConfiguration({ columnConfig }) {
      const aptConfig = GridConfig.getAPTWindowConfig(columnConfig);
  
      const defaultConfig = {
        title: appConstants.gridNames.AddNewShipmentsGrid,
        keyColumn: "proId",
        rowSelection: "multiple",
        rowDeselection: true,
        defaultColDef: {
          sortable: true,
          resizable: true,
        },
        rowClassRules: {
          "ag-row-disabled": (params) => params.data.isInPlan,
        },
        columns: [
          createCustomCheckboxColumn("isInPlan"),
          {
            headerName: "Pro #",
            field: "proId",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_140,
            filterParams:{
              filterType: appConstants.FILTER_TYPES.STRING
            },
          },
          {
            headerName: "Trailer #",
            field: "trailerNumber",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_100,
          },
          {
            headerName: "SCAC Code",
            headerTooltip: "SCAC Code",
            field: "trailerScac",
            hide: true,
            filter: MultiSelectFilter,
            width: appConstants.columnWidth.W_120,
            minWidth: appConstants.columnWidth.W_120,
            cellClass: "gridCell-leftAlign",
          },
          {
            headerName: "Spl Needs",
            headerTooltip: "Special Needs",
            minWidth: appConstants.columnWidth.SPL_NEED,
            cellClass: "gridCell-leftAlign",
            valueGetter: SpecialNeedsValueGetter,
            filter: StaticFilter,
            filterParams: {
              getStaticOptions: GridConfig.getSpecialNeedsOption,
            },
            cellRendererParams: {
              getAPTWindow: aptConfig.valueGetter,
            },
            cellRenderer: SpecialNeedsRenderer,
          },
          {
            headerName: "Customer",
            field: "displayCustomerName",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.CUSTOMER,
            cellRenderer: CustomerNameCellRenderer,
          },
          {
            headerName: "Return Reason",
            field: "returnCode",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_120,
          },
          {
            headerName: "Due Date",
            field: "shipmentDueDate",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_120,
          },
          {
            headerName: "Address",
            field: "customerAddress",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.CUSTOMER_ADDRESS,
            tooltipField: "customerAddress",
          },
          {
            headerName: "City",
            field: "customerCity",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.CITY,
          },
          {
            headerName: "State",
            field: "customerState",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_80,
          },
          {
            headerName: "Zip",
            field: "customerZip",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_85,
          },
          {
            headerName: aptConfig.headerName,
            headerTooltip: aptConfig.headerTooltip,
            field: "shipmentCTA",
            minWidth: appConstants.columnWidth.EARLY_LATE_CTA,
            cellClass: "gridCell-leftAlign",
            filter: DateTimeFilter,
            filterParams: {
              DATETYPE: appConstants.DATETYPES.TIME,
              isTimeInterval: true,
              useGetterValue: true,
            },
            valueGetter: aptConfig.valueGetter,
            valueFormatter: aptConfig.valueFormatter,
            tooltipValueGetter: (params) => params.value,
          },
          {
            headerName: "Delivery Window",
            headerTooltip: "Delivery Window",
            field: "deliveryWindow",
            minWidth: appConstants.columnWidth.DELIVERY_WINDOW,
            cellClass: "gridCell-leftAlign",
            filter: DateTimeFilter,
            filterParams: {
              DATETYPE: appConstants.DATETYPES.TIME,
              isTimeInterval: true,
              useGetterValue: true,
            },
            editable: false,
            valueFormatter: formatDeliveryWindow,
          },
          {
            headerName: "IB ETA",
            headerTooltip: "IB ETA",
            field: "inboundArrivalTime",
            minWidth: appConstants.columnWidth.IBETA,
            valueFormatter: GridConfig.formatDateTime,
            cellRenderer: IBETARenderer,
            filter: DateTimeFilter,
            filterParams: {
              DATETYPE: appConstants.DATETYPES.DATETIME,
            },
          },
          {
            headerName: "Shipment Status",
            field: "shipmentStatus",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_140,
            tooltipField: "shipmentStatus",
          },
          {
            headerName: "Orig Term",
            headerTooltip: "Origin Terminal",
            field: "originTerminal",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_110,
          },
          {
            headerName: "Current Loc",
            headerTooltip: "Current Location",
            field: "currentTerminal",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_110,
          },
          {
            headerName: "Next Loc",
            headerTooltip: "Next Location",
            field: "nextTerminal",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_110,
          },
          {
            headerName: "Apt Due Date",
            headerTooltip: "Apt Due Date",
            field: "aptDueDate",
            minWidth: appConstants.columnWidth.APT_DUE_DATE,
            cellClass: "gridCell-leftAlign",
            valueFormatter:formatDate,
            filter: DateTimeFilter,
            filterParams: {
              DATETYPE: appConstants.DATETYPES.DATE,
            }
          },
          {
            headerName: "Trailer Dest",
            headerTooltip: "Trailer Destination",
            field: "trailerDestinationTerminal",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_120,
          },
          {
            headerName: "HU",
            field: "hu",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_80,
          },
          {
            headerName: "Weight",
            field: "weight",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_95,
          },
          {
            headerName: "Pieces",
            field: "pieces",
            cellClass: "gridCell-leftAlign",
            filter: MultiSelectFilter,
            minWidth: appConstants.columnWidth.W_95,
          },
        ],
      };
      if (columnConfig === COLUMN_CONFIG.SAIA) {
        defaultConfig.columns = defaultConfig.columns.filter(
          (column) => column.field !== "deliveryWindow"
        );
      }
      return defaultConfig;
    }
  }
  